import React from 'react';
import { GoalsData } from '../../types';

interface GoalsTabProps {
    goalsData: GoalsData | null | undefined;
    loading: boolean;
    error: string | null;
}

const GoalsTab: React.FC<GoalsTabProps> = ({ goalsData, loading, error }) => {
    const sectionShell =
        'rounded-2xl border border-slate-200/80 bg-white/95 shadow-[0_18px_40px_rgba(15,42,79,0.08)] overflow-hidden';
    const cardShell =
        'group relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(15,42,79,0.12)]';

    const hasGoals = Boolean(goalsData?.key_goals?.length);
    const hasPriorities = Boolean(goalsData?.priority_goals?.length);
    const hasTrends = Boolean(goalsData?.trend_goals?.length);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-red-500 p-4">
                Error: {error}
            </div>
        );
    }

    return (
        <div className="mx-auto px-4 space-y-6">
            {/* Key Goals Section */}
            <div className={sectionShell}>
                <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-sky-50 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center rounded-full bg-[#0F2A4F] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white">
                            Goals
                        </span>
                        <h2 className="text-2xl font-bold text-[#0F2A4F]">Key Goals</h2>
                    </div>
                </div>
                <div className="p-5">
                    {hasGoals ? (
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                            {goalsData?.key_goals.map((goal, index) => (
                                <div
                                    key={index}
                                    className={cardShell}
                                >
                                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#859DB5] via-[#0F71C0] to-[#8EC5FC]" />
                                    <div className="mb-2.5 flex items-center gap-3">
                                        <h3 className="text-lg font-semibold text-slate-900">{goal.goal}</h3>
                                    </div>
                                    <p className="text-sm leading-5 text-slate-600">{goal.definition}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-slate-500">
                            No key goals found.
                        </div>
                    )}
                </div>
            </div>

            {/* Priorities Section */}
            <div className={sectionShell}>
                <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-blue-50 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center rounded-full bg-[#0F71C0] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white">
                            Priorities
                        </span>
                        <h2 className="text-2xl font-bold text-[#0F2A4F]">Priorities</h2>
                    </div>
                </div>
                <div className="p-5">
                    {hasPriorities ? (
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                            {goalsData?.priority_goals.map((priority, index) => (
                                <div
                                    key={index}
                                    className={cardShell}
                                >
                                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0F71C0] via-[#3B82F6] to-[#93C5FD]" />
                                    <div className="mb-3 flex flex-wrap items-center gap-2.5">
                                        <h3 className="text-lg font-semibold text-slate-900">{priority.name}</h3>
                                        <span className="inline-flex items-center rounded-full bg-[#0F2A4F]/7 px-3 py-1 text-xs font-semibold tracking-wide text-[#0F2A4F]">
                                            {priority.pillar_alignment}
                                        </span>
                                    </div>
                                    <div className="space-y-2.5">
                                        {priority.details.map((detail, detailIndex) => (
                                            <div key={detailIndex} className="flex items-start gap-3 rounded-lg bg-slate-50/90 px-3.5 py-2.5">
                                                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#0F71C0]" />
                                                <p className="text-sm leading-5 text-slate-700">{detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-slate-500">
                            No priorities found.
                        </div>
                    )}
                </div>
            </div>

            {/* Industry Trends Section */}
            <div className={sectionShell}>
                <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-amber-50 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center rounded-full bg-[#D99100] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white">
                            Trends
                        </span>
                        <h2 className="text-2xl font-bold text-[#0F2A4F]">Industry Trends</h2>
                    </div>
                </div>
                <div className="p-5">
                    {hasTrends ? (
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                            {goalsData?.trend_goals.map((trend, index) => (
                                <div
                                    key={index}
                                    className={cardShell}
                                >
                                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FFCD73] via-[#F59E0B] to-[#F97316]" />
                                    <div className="mb-3 space-y-2.5">
                                        <h3 className="text-lg font-semibold text-slate-900">{trend.IndustryTrend}</h3>
                                        <div className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold tracking-wide text-amber-800 ring-1 ring-amber-200">
                                            {trend.TrendHighlights}
                                        </div>
                                    </div>
                                    <div className="space-y-2.5">
                                        {trend.TrendDetails.map((detail, detailIndex) => (
                                            <div key={detailIndex} className="flex items-start gap-3 rounded-lg bg-amber-50/60 px-3.5 py-2.5">
                                                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
                                                <p className="text-sm leading-5 text-slate-700">{detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-slate-500">
                            No industry trends found.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GoalsTab;
